<template>
  <HrPageLayout
    title="Daily Attendance Report"
    subtitle="Daily attendance by date and department"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Reports' },
      { label: 'Daily Attendance' }
    ]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-primary" @click="loadAttendanceData" :disabled="isLoading">
        <i class="bx bx-refresh hr-me-1"></i>
        <span v-if="isLoading">Loading...</span>
        <span v-else>Refresh</span>
      </button>
      <button class="hr-btn hr-btn-success" @click="exportToExcel" :disabled="isExporting || !reportData.length">
        <i class="bx bx-file hr-me-1"></i>
        <span v-if="isExporting">Exporting...</span>
        <span v-else>Export Excel</span>
      </button>
    </template>

    <div class="report-daily-content">
      <!-- Date and filters (Common Header) -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-form-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Report Date</label>
              <input type="date" class="hr-input" v-model="selectedDate" @change="loadAttendanceData" :max="maxDate">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select class="hr-select" v-model="selectedDepartment" @change="loadAttendanceData">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Sleek Tabs -->
      <div class="hr-tabs hr-mb-4" style="border-bottom: 1px solid #e2e8f0; display: flex; gap: 0.5rem;">
        <button type="button" @click="activeTab = 'data'" style="padding: 0.5rem 1rem; font-size: 0.875rem; background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; font-weight: 600; transition: all 0.2s;" :style="activeTab === 'data' ? 'border-bottom-color: #3b82f6; color: #3b82f6;' : 'color: #64748b;'">
          <i class="bx bx-table hr-me-1"></i> Data View
        </button>
        <button type="button" @click="activeTab = 'summary'" style="padding: 0.5rem 1rem; font-size: 0.875rem; background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; font-weight: 600; transition: all 0.2s;" :style="activeTab === 'summary' ? 'border-bottom-color: #3b82f6; color: #3b82f6;' : 'color: #64748b;'">
          <i class="bx bx-pie-chart-alt-2 hr-me-1"></i> Summary & Metrics
        </button>
      </div>

      <!-- Summary Tab -->
      <div v-show="activeTab === 'summary'">
      <!-- System configuration -->
      <div class="hr-chart-card hr-mb-4" v-if="isAdmin">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">System Configuration</h5>
          <button class="hr-btn hr-btn-sm hr-btn-primary" @click="saveConfiguration" :disabled="!configurationChanged">
            <i class="bx bx-save hr-me-1"></i>
            <span v-if="configSaving">Saving...</span>
            <span v-else>Save Changes</span>
          </button>
        </div>
        <div class="hr-chart-body">
          <div class="hr-form-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Enable Roll Calls</label>
              <label class="hr-flex hr-align-center hr-gap-2">
                <input type="checkbox" class="hr-input" v-model="configuration.enableRollCalls" @change="onConfigurationChange">
                <span class="hr-small hr-text-muted" v-if="configuration.enableRollCalls">Employees marked absent if they miss required roll calls.</span>
                <span class="hr-small hr-text-muted" v-else>Absence from check-in/check-out only.</span>
              </label>
            </div>
            <div class="hr-filter-group" v-if="configuration.enableRollCalls">
              <label class="hr-form-label">Minimum Roll Calls Required</label>
              <select class="hr-select" v-model="configuration.minRequiredRollCalls" @change="onConfigurationChange">
                <option value="1">At least 1 roll call</option>
                <option value="2">At least 2 roll calls</option>
                <option value="3">All 3 roll calls</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Minimum Working Hours</label>
              <div class="hr-flex hr-gap-2 hr-align-center">
                <input type="number" class="hr-input" style="max-width:100px" v-model="configuration.minWorkingHours" min="0" max="24" step="0.5" @change="onConfigurationChange">
                <span class="hr-text-muted">hours</span>
              </div>
              <span class="hr-form-hint">Employees with fewer hours will be highlighted</span>
            </div>
            <div class="hr-filter-group">
              <label class="hr-flex hr-align-center hr-gap-2">
                <input type="checkbox" class="hr-input" v-model="configuration.strictMode" @change="onConfigurationChange">
                <span>Strict Attendance Mode</span>
              </label>
              <span class="hr-form-hint">Both check-in and check-out required to be present</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
          <p class="hr-metric-label">Total Employees</p>
          <h2 class="hr-metric-value">{{ dashboardStats.totalEmployees }}</h2>
          <p class="hr-metric-hint"><span class="hr-text-success">{{ dashboardStats.presentEmployees }}</span> Present / <span class="hr-text-danger">{{ dashboardStats.absentEmployees }}</span> Absent</p>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-calendar-check"></i></div>
          <p class="hr-metric-label">Total Check-ins</p>
          <h2 class="hr-metric-value">{{ dashboardStats.totalCheckins }}</h2>
          <p class="hr-metric-hint"><span class="hr-text-primary">{{ dashboardStats.remoteCheckins }}</span> Remote / <span class="hr-text-success">{{ dashboardStats.onsiteCheckins }}</span> Onsite</p>
        </div>
        <div class="hr-metric-card gradient-4" v-if="configuration.enableRollCalls">
          <div class="hr-metric-icon"><i class="bx bx-check-double"></i></div>
          <p class="hr-metric-label">Roll Call Response</p>
          <h2 class="hr-metric-value">{{ dashboardStats.rollCallResponses }}</h2>
          <p class="hr-metric-hint">{{ rollCallResponseRate }}% response rate</p>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <p class="hr-metric-label">Total Hours Worked</p>
          <h2 class="hr-metric-value">{{ dashboardStats.totalHours }} hrs</h2>
          <p class="hr-metric-hint">Avg {{ dashboardStats.averageHours }} hrs/employee</p>
        </div>
      </div>
      </div> <!-- End Summary Tab -->

      <!-- Data Tab -->
      <div v-show="activeTab === 'data'">
      <!-- Search and Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-flex hr-flex-wrap hr-gap-2 hr-align-center" style="justify-content: space-between;">
            <div class="hr-search-wrap" style="flex:1; max-width: 320px;">
              <input type="text" class="hr-input" placeholder="Search by name or employee ID..." v-model="searchQuery" @input="handleSearch">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-flex hr-gap-1 hr-flex-wrap">
              <button type="button" class="hr-btn hr-btn-outline-primary" :class="{ 'hr-btn-primary': attendanceFilter === 'all' }" @click="setAttendanceFilter('all')">All</button>
              <button type="button" class="hr-btn hr-btn-outline-success" :class="{ 'hr-btn-success': attendanceFilter === 'present' }" @click="setAttendanceFilter('present')">Present</button>
              <button type="button" class="hr-btn hr-btn-outline-danger" :class="{ 'hr-btn-danger': attendanceFilter === 'absent' }" @click="setAttendanceFilter('absent')">Absent</button>
              <button type="button" class="hr-btn hr-btn-outline-warning" :class="{ 'hr-btn-warning': attendanceFilter === 'late' }" @click="setAttendanceFilter('late')" v-if="configuration.enableRollCalls">Missed Roll Calls</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Attendance Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div v-if="isLoading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading attendance data...</p>
          </div>
          <div v-else class="hr-table-wrapper position-relative">
                <table class="hr-table hr-table-hover">
                  <thead>
                  <tr>
                    <th @click="sortTable('empNo')" class="cursor-pointer">
                      Emp No. <i :class="getSortIcon('empNo')"></i>
                    </th>
                    <th @click="sortTable('employeeName')" class="cursor-pointer">
                      Employee Name <i :class="getSortIcon('employeeName')"></i>
                    </th>
                    <th>Department</th>
                    <th class="text-center">Check-in</th>
                    <th class="text-center">Check-out</th>
                    <th class="text-center">Remote Check-in</th>
                    <th class="text-center">Remote Check-out</th>
                    <th class="text-center">Hours Worked</th>
                    <th v-if="configuration.enableRollCalls" class="text-center">Roll Call 1</th>
                    <th v-if="configuration.enableRollCalls" class="text-center">Roll Call 2</th>
                    <th v-if="configuration.enableRollCalls" class="text-center">Roll Call 3</th>
                    <th class="text-center">Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="record in paginatedData" :key="record.empNo"
                      :class="getRowClass(record)">
                    <td>{{ record.empNo }}</td>
                    <td>{{ record.employeeName }}</td>
                    <td>{{ record.departmentName }}</td>

                    <!-- Check-in -->
                    <td class="text-center">
                      <div v-if="record.checkInTime && record.checkInTime !== '-'">
                          <span :class="getTimeStatusClass(record.checkInTime, 'checkin')">
                            {{ record.checkInTime }}
                          </span>
                        <small class="d-block hr-text-muted" v-if="record.checkInLocation">
                          {{ truncateLocation(record.checkInLocation) }}
                        </small>
                      </div>
                      <span v-else class="hr-text-danger">-</span>
                    </td>

                    <!-- Check-out -->
                    <td class="text-center">
                      <div v-if="record.checkOutTime && record.checkOutTime !== '-'">
                        <span>{{ record.checkOutTime }}</span>
                        <small class="d-block hr-text-muted" v-if="record.checkOutLocation">
                          {{ truncateLocation(record.checkOutLocation) }}
                        </small>
                      </div>
                      <span v-else class="hr-text-danger">-</span>
                    </td>

                    <!-- Remote Check-in -->
                    <td class="text-center">
                      <div v-if="record.remoteCheckInTime && record.remoteCheckInTime !== '-'">
                          <span :class="getTimeStatusClass(record.remoteCheckInTime, 'checkin')">
                            {{ record.remoteCheckInTime }}
                          </span>
                        <small class="d-block hr-text-muted" v-if="record.remoteCheckInLocation">
                          {{ truncateLocation(record.remoteCheckInLocation) }}
                        </small>
                        <a v-if="record.remoteCheckInLat || record.remoteCheckInLatitude" :href="`https://maps.google.com/?q=${record.remoteCheckInLat || record.remoteCheckInLatitude},${record.remoteCheckInLong || record.remoteCheckInLongitude}`" target="_blank" class="hr-link hr-small d-block mt-1">
                          <i class="bx bx-map"></i> View Map
                        </a>
                      </div>
                      <span v-else class="hr-text-danger">-</span>
                    </td>

                    <!-- Remote Check-out -->
                    <td class="text-center">
                      <div v-if="record.remoteCheckOutTime && record.remoteCheckOutTime !== '-'">
                        <span>{{ record.remoteCheckOutTime }}</span>
                        <small class="d-block hr-text-muted" v-if="record.remoteCheckOutLocation">
                          {{ truncateLocation(record.remoteCheckOutLocation) }}
                        </small>
                        <a v-if="record.remoteCheckOutLat || record.remoteCheckOutLatitude" :href="`https://maps.google.com/?q=${record.remoteCheckOutLat || record.remoteCheckOutLatitude},${record.remoteCheckOutLong || record.remoteCheckOutLongitude}`" target="_blank" class="hr-link hr-small d-block mt-1">
                          <i class="bx bx-map"></i> View Map
                        </a>
                      </div>
                      <span v-else class="hr-text-danger">-</span>
                    </td>

                    <!-- Hours Worked -->
                    <td class="text-center">
                        <span :class="getHoursClass(record.hoursWorked)">
                          {{ record.hoursWorked || '-' }}
                        </span>
                    </td>

                    <!-- Roll Calls -->
                    <td v-if="configuration.enableRollCalls" class="text-center">
                      <div v-if="record.rollCall1 && record.rollCall1 !== '-'" class="hr-text-success">
                        <i class="bx bx-check-circle"></i> {{ record.rollCall1 }}
                        <small class="d-block hr-text-muted" v-if="record.rollCall1Location">
                          {{ truncateLocation(record.rollCall1Location) }}
                        </small>
                      </div>
                      <span v-else class="hr-text-danger"><i class="bx bx-x-circle"></i></span>
                    </td>

                    <td v-if="configuration.enableRollCalls" class="text-center">
                      <div v-if="record.rollCall2 && record.rollCall2 !== '-'" class="hr-text-success">
                        <i class="bx bx-check-circle"></i> {{ record.rollCall2 }}
                        <small class="d-block hr-text-muted" v-if="record.rollCall2Location">
                          {{ truncateLocation(record.rollCall2Location) }}
                        </small>
                      </div>
                      <span v-else class="hr-text-danger"><i class="bx bx-x-circle"></i></span>
                    </td>

                    <td v-if="configuration.enableRollCalls" class="text-center">
                      <div v-if="record.rollCall3 && record.rollCall3 !== '-'" class="hr-text-success">
                        <i class="bx bx-check-circle"></i> {{ record.rollCall3 }}
                        <small class="d-block hr-text-muted" v-if="record.rollCall3Location">
                          {{ truncateLocation(record.rollCall3Location) }}
                        </small>
                      </div>
                      <span v-else class="hr-text-danger"><i class="bx bx-x-circle"></i></span>
                    </td>

                    <!-- Status -->
                    <td class="text-center">
                        <span :class="getStatusBadgeClass(record)">
                          {{ getEmployeeStatus(record) }}
                        </span>
                    </td>
                  </tr>

                  <!-- No data row -->
                  <tr v-if="filteredData.length === 0 && !isLoading">
                    <td :colspan="getTableColumnCount()" class="hr-text-center hr-py-4">
                      <div class="hr-empty-state">
                        <div class="hr-empty-icon"><i class="bx bx-info-circle"></i></div>
                        <p>No attendance records found for the selected criteria.</p>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
          </div>

          <div class="hr-pagination-wrap hr-flex-between hr-mt-4" v-if="filteredData.length > 0">
            <div class="hr-text-muted hr-small">Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalRecords }} entries</div>
            <nav class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="currentPage === 1" @click.prevent="changePage(currentPage - 1)"><i class="bx bx-chevron-left"></i></button>
              <button v-for="page in displayedPages" :key="page" type="button" class="hr-page-btn" :class="{ active: currentPage === page }" @click.prevent="changePage(page)">{{ page }}</button>
              <button type="button" class="hr-page-btn" :disabled="currentPage === totalPages" @click.prevent="changePage(currentPage + 1)"><i class="bx bx-chevron-right"></i></button>
            </nav>
          </div>
        </div>
      </div>
      </div> <!-- End Data Tab -->
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";
import moment from "moment";
import _ from "lodash";
import * as XLSX from 'xlsx';

export default {
  name: "ReportDaily",
  components: { HrPageLayout },
  data() {
    return {
      activeTab: 'data', // Tab state
      // User permissions
      isAdmin: false,

      // Configuration
      configuration: {
        enableRollCalls: true,
        minRequiredRollCalls: 3,
        minWorkingHours: 8,
        strictMode: true,
      },
      configurationChanged: false,
      configSaving: false,

      // Date and filters
      selectedDate: moment().format('YYYY-MM-DD'),
      maxDate: moment().format('YYYY-MM-DD'),
      selectedDepartment: '',
      searchQuery: '',
      attendanceFilter: 'all',

      // Data
      departments: [],
      reportData: [],
      filteredData: [],
      dashboardStats: {
        totalEmployees: 0,
        presentEmployees: 0,
        absentEmployees: 0,
        totalCheckins: 0,
        remoteCheckins: 0,
        onsiteCheckins: 0,
        rollCallResponses: 0,
        totalHours: '0',
        averageHours: '0'
      },

      // Pagination
      currentPage: 1,
      itemsPerPage: 20,

      // Sorting
      sortColumn: 'empNo',
      sortDirection: 'asc',

      // Loading states
      isLoading: false,
      isExporting: false,

      // Debounced search
      debouncedSearch: null
    };
  },

  computed: {
    totalRecords() {
      return this.filteredData.length;
    },

    totalPages() {
      return Math.ceil(this.totalRecords / this.itemsPerPage);
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredData.slice(start, end);
    },

    paginationStart() {
      return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
    },

    displayedPages() {
      const totalDisplayed = 5;
      const halfDisplay = Math.floor(totalDisplayed / 2);

      if (this.totalPages <= totalDisplayed) {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }

      if (this.currentPage <= halfDisplay + 1) {
        return Array.from({ length: totalDisplayed }, (_, i) => i + 1);
      }

      if (this.currentPage >= this.totalPages - halfDisplay) {
        return Array.from({ length: totalDisplayed }, (_, i) => this.totalPages - totalDisplayed + i + 1);
      }

      return Array.from({ length: totalDisplayed }, (_, i) => this.currentPage - halfDisplay + i);
    },

    rollCallResponseRate() {
      if (!this.configuration.enableRollCalls || this.dashboardStats.totalEmployees === 0) {
        return 0;
      }
      const totalPossibleResponses = this.dashboardStats.totalEmployees * 3;
      return Math.round((this.dashboardStats.rollCallResponses / totalPossibleResponses) * 100);
    }
  },

  created() {
    this.debouncedSearch = _.debounce(this.applyFilters, 300);
    this.initializeComponent();
  },

  methods: {
    async initializeComponent() {
      await this.checkUserRole();
      await this.loadConfiguration();
      await this.loadDepartments();
      await this.loadAttendanceData();
    },

    // API calls
    async checkUserRole() {
      try {
        const data = new FormData();
        data.append("function", "getUserRole");

        const response = await execute(data, authUrl);
        if (response.data.success) {
          this.isAdmin = response.data.data?.role === 'admin';
        }
      } catch (error) {
        console.error("Error checking user role:", error);
      }
    },

    async loadConfiguration() {
      try {
        const data = new FormData();
        data.append("function", "getSystemConfiguration");

        const response = await execute(data, authUrl);
        if (response.data.success && response.data.data?.configuration) {
          const config = response.data.data.configuration;
          this.configuration = {
            enableRollCalls: config.enableRollCalls ?? true,
            minRequiredRollCalls: parseInt(config.minRequiredRollCalls) || 3,
            minWorkingHours: parseFloat(config.minWorkingHours) || 8,
            strictMode: config.strictMode ?? true
          };
        }
      } catch (error) {
        console.error("Error loading configuration:", error);
      }
    },

    async saveConfiguration() {
      if (!this.configurationChanged) return;

      this.configSaving = true;
      try {
        const data = new FormData();
        data.append("function", "saveSystemConfiguration");
        data.append("configuration", JSON.stringify(this.configuration));

        const response = await execute(data, authUrl);
        if (response.data.success) {
          this.configurationChanged = false;
          this.$toast.success('Configuration saved successfully');
          await this.loadAttendanceData(); // Refresh data with new configuration
        } else {
          this.$toast.error('Failed to save configuration');
        }
      } catch (error) {
        console.error("Error saving configuration:", error);
        this.$toast.error('Error saving configuration');
      } finally {
        this.configSaving = false;
      }
    },

    async loadDepartments() {
      try {
        const data = new FormData();
        data.append("function", "getDepartments");

        const response = await execute(data, authUrl);
        if (response.data.success && response.data.data?.departments) {
          this.departments = response.data.data.departments;
        }
      } catch (error) {
        console.error("Error loading departments:", error);
      }
    },

    async loadAttendanceData() {
      this.isLoading = true;
      try {
        const data = new FormData();
        data.append("function", "getDailyAttendanceReportProcessed");
        data.append("date", this.selectedDate);
        data.append("configuration", JSON.stringify(this.configuration));

        if (this.selectedDepartment) {
          data.append("departmentId", this.selectedDepartment);
        }

        if (this.searchQuery.trim()) {
          data.append("searchQuery", this.searchQuery.trim());
        }

        const response = await execute(data, authUrl);
        if (response.data.success) {
          this.reportData = response.data.data.attendanceRecords || [];
          this.dashboardStats = response.data.data.dashboardStats || this.dashboardStats;
          this.applyFilters();
        } else {
          this.$toast.error(response.data.message || 'Failed to load attendance data');
          this.reportData = [];
          this.filteredData = [];
        }
      } catch (error) {
        console.error("Error loading attendance data:", error);
        this.$toast.error('Error loading attendance data');
        this.reportData = [];
        this.filteredData = [];
      } finally {
        this.isLoading = false;
      }
    },

    async exportToExcel() {
      if (this.filteredData.length === 0) {
        this.$toast.warning('No data to export');
        return;
      }

      this.isExporting = true;
      try {
        // Prepare export data
        const exportData = this.filteredData.map(record => {
          const baseRecord = {
            'Employee No': record.empNo,
            'Employee Name': record.employeeName,
            'Department': record.departmentName,
            'Check-in': record.checkInTime !== '-' ? record.checkInTime : '',
            'Check-out': record.checkOutTime !== '-' ? record.checkOutTime : '',
            'Remote Check-in': record.remoteCheckInTime !== '-' ? record.remoteCheckInTime : '',
            'Remote Check-in Coordinates': this.formatCoordinates(record.remoteCheckInLat, record.remoteCheckInLong, record.remoteCheckInLatitude, record.remoteCheckInLongitude),
            'Remote Check-out': record.remoteCheckOutTime !== '-' ? record.remoteCheckOutTime : '',
            'Remote Check-out Coordinates': this.formatCoordinates(record.remoteCheckOutLat, record.remoteCheckOutLong, record.remoteCheckOutLatitude, record.remoteCheckOutLongitude),
            'Hours Worked': record.hoursWorked || '',
            'Status': this.getEmployeeStatus(record)
          };

          if (this.configuration.enableRollCalls) {
            baseRecord['Roll Call 1'] = record.rollCall1 !== '-' ? record.rollCall1 : '';
            baseRecord['Roll Call 2'] = record.rollCall2 !== '-' ? record.rollCall2 : '';
            baseRecord['Roll Call 3'] = record.rollCall3 !== '-' ? record.rollCall3 : '';
          }

          return baseRecord;
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Set column widths
        const columnWidths = [
          { wch: 15 }, // Employee No
          { wch: 25 }, // Employee Name
          { wch: 20 }, // Department
          { wch: 15 }, // Check-in
          { wch: 15 }, // Check-out
          { wch: 15 }, // Remote Check-in
          { wch: 22 }, // Remote Check-in Coordinates
          { wch: 15 }, // Remote Check-out
          { wch: 22 }, // Remote Check-out Coordinates
          { wch: 15 }, // Hours Worked
        ];

        if (this.configuration.enableRollCalls) {
          columnWidths.push({ wch: 15 }); // Roll Call 1
          columnWidths.push({ wch: 15 }); // Roll Call 2
          columnWidths.push({ wch: 15 }); // Roll Call 3
        }

        columnWidths.push({ wch: 15 }); // Status

        ws['!cols'] = columnWidths;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Daily Attendance");

        // Generate filename
        let filename = `Daily_Attendance_${this.selectedDate}`;
        if (this.selectedDepartment) {
          const dept = this.departments.find(d => d.id == this.selectedDepartment);
          if (dept) {
            filename += `_${dept.departmentName.replace(/\s+/g, '_')}`;
          }
        }
        if (this.attendanceFilter !== 'all') {
          filename += `_${this.attendanceFilter}`;
        }
        filename += '.xlsx';

        // Write and download file
        XLSX.writeFile(wb, filename);
        this.$toast.success('Export completed successfully');
      } catch (error) {
        console.error('Export failed:', error);
        this.$toast.error('Export failed');
      } finally {
        this.isExporting = false;
      }
    },

    // Event handlers
    onConfigurationChange() {
      this.configurationChanged = true;
    },

    handleSearch() {
      this.debouncedSearch();
    },

    setAttendanceFilter(filter) {
      this.attendanceFilter = filter;
      this.currentPage = 1;
      this.applyFilters();
    },

    sortTable(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      this.applyFilters();
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    // Filtering and processing
    applyFilters() {
      let filtered = [...this.reportData];

      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(record =>
            record.employeeName?.toLowerCase().includes(query) ||
            record.empNo?.toLowerCase().includes(query)
        );
      }

      // Apply attendance filter
      switch (this.attendanceFilter) {
        case 'present':
          filtered = filtered.filter(record => this.isEmployeePresent(record));
          break;
        case 'absent':
          filtered = filtered.filter(record => this.isEmployeeAbsent(record));
          break;
        case 'late':
          if (this.configuration.enableRollCalls) {
            filtered = filtered.filter(record => this.hasMissedRollCalls(record));
          }
          break;
      }

      // Apply sorting
      filtered.sort((a, b) => {
        const aValue = a[this.sortColumn] || '';
        const bValue = b[this.sortColumn] || '';

        const comparison = aValue.toString().localeCompare(bValue.toString());
        return this.sortDirection === 'asc' ? comparison : -comparison;
      });

      this.filteredData = filtered;
      this.currentPage = 1;
    },

    // Helper methods
    isEmployeePresent(record) {
      const hasCheckin = (record.checkInTime && record.checkInTime !== '-') ||
          (record.remoteCheckInTime && record.remoteCheckInTime !== '-');

      if (!hasCheckin) return false;

      if (this.configuration.strictMode) {
        const hasCheckout = (record.checkOutTime && record.checkOutTime !== '-') ||
            (record.remoteCheckOutTime && record.remoteCheckOutTime !== '-');
        if (!hasCheckout) return false;
      }

      if (this.configuration.enableRollCalls) {
        const missedRollCalls = this.getMissedRollCallsCount(record);
        const maxAllowedMissed = 3 - this.configuration.minRequiredRollCalls;
        if (missedRollCalls > maxAllowedMissed) return false;
      }

      const hoursWorked = this.parseHours(record.hoursWorked);
      if (hoursWorked < this.configuration.minWorkingHours) return false;

      return true;
    },

    isEmployeeAbsent(record) {
      return !this.isEmployeePresent(record);
    },

    hasMissedRollCalls(record) {
      if (!this.configuration.enableRollCalls) return false;
      const missedCount = this.getMissedRollCallsCount(record);
      return missedCount > 0;
    },

    getMissedRollCallsCount(record) {
      let missed = 0;
      if (!record.rollCall1 || record.rollCall1 === '-') missed++;
      if (!record.rollCall2 || record.rollCall2 === '-') missed++;
      if (!record.rollCall3 || record.rollCall3 === '-') missed++;
      return missed;
    },

    parseHours(hoursStr) {
      if (!hoursStr || hoursStr === '-') return 0;
      const match = hoursStr.match(/(\d+\.?\d*)/);
      return match ? parseFloat(match[1]) : 0;
    },

    getEmployeeStatus(record) {
      if (this.isEmployeePresent(record)) {
        return 'Present';
      } else if (this.configuration.enableRollCalls && this.hasMissedRollCalls(record)) {
        return 'Missed Roll Calls';
      } else {
        return 'Absent';
      }
    },

    // UI helper methods
    getRowClass(record) {
      const status = this.getEmployeeStatus(record);
      switch (status) {
        case 'Present':
          return '';
        case 'Missed Roll Calls':
          return 'table-warning';
        case 'Absent':
          return 'table-danger';
        default:
          return '';
      }
    },

    getStatusBadgeClass(record) {
      const status = this.getEmployeeStatus(record);
      switch (status) {
        case 'Present':
          return 'hr-badge hr-badge-success';
        case 'Missed Roll Calls':
          return 'hr-badge hr-badge-warning';
        case 'Absent':
          return 'hr-badge hr-badge-danger';
        default:
          return 'hr-badge hr-badge-secondary';
      }
    },

    getTimeStatusClass(timeStr, type) {
      if (!timeStr || timeStr === '-') return 'hr-text-danger';

      // You can add logic here to highlight late arrivals based on shift times
      // For now, just return default styling
      return '';
    },

    getHoursClass(hoursStr) {
      const hours = this.parseHours(hoursStr);
      if (hours === 0) return 'hr-text-muted';
      if (hours < this.configuration.minWorkingHours) return 'hr-text-danger hr-fw-bold';
      if (hours < this.configuration.minWorkingHours + 1) return 'hr-text-warning hr-fw-bold';
      return 'hr-text-success hr-fw-bold';
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'bx bx-sort';
      }
      return this.sortDirection === 'asc' ? 'bx bx-sort-up' : 'bx bx-sort-down';
    },

    truncateLocation(location) {
      if (!location || location === '-') return '-';
      return location.length > 20 ? location.substring(0, 20) + '...' : location;
    },

    formatCoordinates(lat1, lng1, lat2, lng2) {
      const a = parseFloat(lat1 ?? lat2);
      const b = parseFloat(lng1 ?? lng2);
      if (Number.isNaN(a) || Number.isNaN(b)) return '-';
      return `${a}, ${b}`;
    },

    getTableColumnCount() {
      let count = 11; // Base columns (incl. Remote Check-in/out Coordinates)
      if (this.configuration.enableRollCalls) {
        count += 3; // Roll call columns
      }
      return count;
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.report-daily-content { padding: 0; }
.hr-metric-hint { font-size: 0.8rem; margin-top: 4px; color: #64748b; }
.hr-fw-bold { font-weight: 600; }

.cursor-pointer {
  cursor: pointer;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

.mini-stats-wid {
  border: none;
  box-shadow: 0 0 35px 0 rgba(154, 161, 171, 0.15);
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.btn-group .btn {
  border-radius: 0;
}

.btn-group .btn:first-child {
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.btn-group .btn:last-child {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}
</style>